import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, FileSpreadsheet, CheckCircle, XCircle } from 'lucide-react';
import { useImportLeads } from '@/hooks/useLeads';

interface LeadImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ImportPreview {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  leads: Array<{
    name: string;
    waId: string;
    email: string;
    valid: boolean;
    error?: string;
  }>;
  errors: string[];
}

export function LeadImportModal({ isOpen, onClose }: LeadImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [step, setStep] = useState<'upload' | 'preview' | 'importing'>('upload');

  const importLeads = useImportLeads();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
      setStep('upload');
    }
  };

  const handlePreview = async () => {
    if (!file) return;

    setIsPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('http://localhost:2222/api/leads/import/preview', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
        setStep('preview');
      } else {
        alert('Erro ao processar arquivo');
      }
    } catch (error) {
      alert('Erro ao processar arquivo');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setStep('importing');
    try {
      const formData = new FormData();
      formData.append('file', file);

      await importLeads.mutateAsync(formData);
      onClose();
    } catch (error) {
      alert('Erro ao importar leads');
      setStep('preview');
    }
  };

  const resetModal = () => {
    setFile(null);
    setPreview(null);
    setStep('upload');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar Leads via Excel</DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <div className="space-y-2">
                <Label htmlFor="file-upload" className="cursor-pointer">
                  <div className="text-lg font-medium">Selecione um arquivo Excel</div>
                  <div className="text-sm text-muted-foreground">
                    Formatos aceitos: .xlsx, .xls
                  </div>
                </Label>
                <Input
                  id="file-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Button asChild>
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />
                    Escolher Arquivo
                  </label>
                </Button>
              </div>
            </div>

            {file && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded">
                  <p className="font-medium">Arquivo selecionado:</p>
                  <p className="text-sm text-muted-foreground">{file.name}</p>
                </div>

                <div className="bg-blue-50 p-4 rounded">
                  <p className="font-medium text-blue-800 mb-2">Formato esperado:</p>
                  <div className="text-sm text-blue-700">
                    <p><strong>Coluna A:</strong> Nome do lead</p>
                    <p><strong>Coluna B:</strong> WhatsApp (com ou sem formatação)</p>
                    <p><strong>Coluna C:</strong> Email (opcional)</p>
                    <p className="mt-2 text-xs">A primeira linha será ignorada (cabeçalho)</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={handlePreview} disabled={isPreviewLoading}>
                    {isPreviewLoading ? 'Processando...' : 'Visualizar Dados'}
                  </Button>
                  <Button variant="outline" onClick={resetModal}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded">
                <div className="text-2xl font-bold text-blue-600">{preview.totalRows}</div>
                <div className="text-sm text-blue-600">Total</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded">
                <div className="text-2xl font-bold text-green-600">{preview.validRows}</div>
                <div className="text-sm text-green-600">Válidos</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded">
                <div className="text-2xl font-bold text-red-600">{preview.invalidRows}</div>
                <div className="text-sm text-red-600">Inválidos</div>
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Nome</th>
                    <th className="p-2 text-left">WhatsApp</th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.leads.slice(0, 50).map((lead, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-2">
                        {lead.valid ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </td>
                      <td className="p-2">{lead.name}</td>
                      <td className="p-2">{lead.waId}</td>
                      <td className="p-2">{lead.email || '-'}</td>
                      <td className="p-2 text-red-600 text-xs">{lead.error || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.leads.length > 50 && (
                <div className="p-2 text-center text-sm text-muted-foreground">
                  ... e mais {preview.leads.length - 50} registros
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('upload')}>
                Voltar
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={preview.validRows === 0}
              >
                Importar {preview.validRows} Leads
              </Button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Importando leads...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}